import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { effectiveCardLook, type Look, lookProps, lookSurface } from '@/entities/appearance';
import { usePrefetchCard } from '@/entities/card';
import {
  type Card,
  CardCover,
  DeadlineBadge,
  isSingleUrl,
  type Label,
  useRefreshLinkPreview,
} from '@/entities/board';

import { type AvatarPerson, AvatarStack } from '@/shared/ui';

import { ActionRail, CheckRing } from './CardRail';
import { cardData, isCard } from './dnd';
import { QuickDeadlineDialog } from './QuickDeadlineDialog';
import styles from './Kanban.module.css';

interface CardTileProps {
  card: Card;
  labels: Label[];
  /** The column's default card look (a card with its own look ignores it). */
  columnLook: Look | null;
  /** False while a filter is active: hidden cards would make drop positions ambiguous. */
  draggableEnabled: boolean;
  onOpen: (cardId: string) => void;
  onToggleComplete: (cardId: string, completed: boolean, anchor?: Element) => void;
  onArchive: (cardId: string) => void;
  onUnpin: (cardId: string) => void;
  /** A pin/unpin request for this card is in flight (blocks double clicks). */
  pinPending: boolean;
  /** Executors to show as avatars (stable array: the tile is memoized). */
  assignees?: readonly AvatarPerson[];
  /** May complete / archive / unpin this card (the server enforces it). */
  canEdit?: boolean;
}

const INTENT_DELAY_MS = 150;
const NO_ASSIGNEES: readonly AvatarPerson[] = [];

const indexes = new WeakMap<Label[], Map<string, Label>>();

/** Labels by id, built once per labels array (a board renders hundreds of tiles with it). */
function labelIndex(labels: Label[]): Map<string, Label> {
  let index = indexes.get(labels);
  if (!index) {
    index = new Map(labels.map((label) => [label.id, label]));
    indexes.set(labels, index);
  }
  return index;
}

export const CardTile = memo(function CardTile({
  card,
  labels,
  columnLook,
  draggableEnabled,
  onOpen,
  onToggleComplete,
  onArchive,
  onUnpin,
  pinPending,
  assignees = NO_ASSIGNEES,
  canEdit = true,
}: CardTileProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const columnId = card.placement.column_id;
  const completed = card.completed_at !== null;
  const refreshPreview = useRefreshLinkPreview();
  // Preview mode only while the whole title is still one link (also before the server answers).
  const preview = card.link_preview && isSingleUrl(card.title) ? card.link_preview : null;
  const look = effectiveCardLook(card, { card_look: columnLook });
  // A short pop only when this tile goes active -> completed while mounted (not on first render).
  const [prevCompleted, setPrevCompleted] = useState(completed);
  const [justCompleted, setJustCompleted] = useState(false);
  if (completed !== prevCompleted) {
    setPrevCompleted(completed);
    setJustCompleted(completed);
  }

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    return combine(
      draggable({
        element,
        canDrag: () => draggableEnabled,
        getInitialData: () => cardData(card.id, columnId),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isCard(source.data) && source.data.cardId !== card.id,
        getData: ({ input }) =>
          attachClosestEdge(cardData(card.id, columnId), {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: () => {
          setEdge(null);
        },
      }),
    );
  }, [card.id, columnId, draggableEnabled]);

  const prefetchCard = usePrefetchCard();
  const intentTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const intent = {
    // Only a pause over the title counts as intent; sweeping the pointer across a board does not.
    start: () => {
      clearTimeout(intentTimer.current);
      intentTimer.current = setTimeout(() => {
        void prefetchCard(card.id);
      }, INTENT_DELAY_MS);
    },
    cancel: () => {
      clearTimeout(intentTimer.current);
    },
  };
  useEffect(
    () => () => {
      clearTimeout(intentTimer.current);
    },
    [],
  );
  const byId = labelIndex(labels);
  const cardLabels = card.label_ids
    .map((id) => byId.get(id))
    .filter((label): label is Label => label !== undefined);

  return (
    <div
      ref={ref}
      className={[
        styles.card,
        lookSurface,
        dragging && styles.dragging,
        completed && styles.completed,
        preview && styles.linkCard,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="card-tile"
      {...lookProps(look)}
    >
      {edge && <span className={`${styles.dropLine} ${styles[`drop-${edge}`]}`} aria-hidden />}
      {card.cover ? (
        // An explicit cover always wins over the automatic site image.
        <CardCover cover={card.cover} className={styles.banner} />
      ) : (
        preview && <LinkBanner preview={preview} />
      )}
      {cardLabels.length > 0 && (
        // Segmented color strip inside the left edge (SPECS.md §13.1).
        <span
          className={styles.labelStrip}
          role="img"
          aria-label={t('labels.onCard', {
            names: cardLabels.map((l) => l.name || t('labels.unnamed')).join(', '),
          })}
        >
          {cardLabels.map((label) => (
            <span key={label.id} style={{ background: label.color }} />
          ))}
        </span>
      )}
      <div className={styles.cardBody} data-look-panel>
        <div className={styles.cardHead}>
          <span
            className={`${styles.checkSlot} ${completed ? '' : styles.slotHideable}`}
            data-testid="check-slot"
          >
            <button
              type="button"
              className={[
                styles.check,
                completed && styles.checkOn,
                justCompleted && styles.checkPop,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={completed}
              disabled={!canEdit}
              aria-label={t(completed ? 'cards.markIncomplete' : 'cards.markComplete')}
              title={t(completed ? 'cards.markIncomplete' : 'cards.markComplete')}
              onAnimationEnd={() => {
                setJustCompleted(false);
              }}
              onClick={(event) => {
                onToggleComplete(card.id, !completed, event.currentTarget);
              }}
            >
              {completed ? '✓' : <CheckRing />}
            </button>
          </span>
          <button
            type="button"
            className={styles.cardTitle}
            onPointerEnter={intent.start}
            onFocus={intent.start}
            onPointerLeave={intent.cancel}
            onBlur={intent.cancel}
            onClick={() => {
              intent.cancel();
              onOpen(card.id);
            }}
          >
            {preview ? (preview.title ?? preview.hostname) : card.title}
          </button>
        </div>
        {preview && (
          <div className={styles.linkInfo} data-testid="link-info">
            {preview.description && <p className={styles.linkDescription}>{preview.description}</p>}
            <a
              className={styles.linkHost}
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              title={card.title}
            >
              {preview.site_name ?? preview.hostname}
            </a>
          </div>
        )}
        {card.deadline && (
          <div className={styles.deadlineRow}>
            <DeadlineBadge
              card={card}
              onClick={() => {
                setEditingDeadline(true);
              }}
            />
          </div>
        )}
        {(assignees.length > 0 || card.assignment_kind === 'shared') && (
          <div className={styles.assigneeRow} data-testid="card-assignees">
            {card.assignment_kind === 'shared' ? (
              <span className={styles.sharedBadge} title={t('assignment.shared')}>
                {t('assignment.shared')}
              </span>
            ) : (
              <AvatarStack people={assignees} size="sm" max={3} label={t('assignment.title')} />
            )}
          </div>
        )}
      </div>
      <ActionRail
        card={card}
        pinPending={pinPending}
        onUnpin={onUnpin}
        onArchive={onArchive}
        completed={completed}
        linkPreview={preview !== null}
        refreshing={refreshPreview.isPending || preview?.status === 'pending'}
        onRefreshPreview={() => {
          refreshPreview.mutate(card);
        }}
      />
      {editingDeadline &&
        // Outside the draggable tile: text selection in the form must not start a card drag.
        createPortal(
          <QuickDeadlineDialog
            card={card}
            onClose={() => {
              setEditingDeadline(false);
            }}
          />,
          document.body,
        )}
    </div>
  );
});

/** The picture area of a link card: the site's image, a skeleton while loading, or grey text. */
function LinkBanner({ preview }: { preview: NonNullable<Card['link_preview']> }) {
  const { t } = useTranslation();
  if (preview.image_url) {
    return (
      <img
        className={`${styles.banner} ${styles.linkImage}`}
        data-testid="link-banner"
        src={preview.image_url}
        alt=""
        loading="lazy"
        draggable={false}
      />
    );
  }
  if (preview.status === 'failed') {
    return (
      <div className={`${styles.banner} ${styles.bannerGrey}`} data-testid="link-banner-grey">
        {t('cards.linkUnavailable')}
      </div>
    );
  }
  if (preview.status === 'pending') {
    return (
      <div
        className={`${styles.banner} ${styles.bannerSkeleton}`}
        data-testid="link-banner-loading"
        role="status"
        aria-label={t('cards.linkLoading')}
      />
    );
  }
  return null;
}
